import React, { useState, useEffect } from 'react';
import AdminLayout from '../components/layout/AdminLayout';
import { getUsers, deleteUser, createUser, updateUser } from '../services/UserService';
import { IUser } from '../types/User';
import { useToast } from '../hooks/useToast';
import { useConfirm } from '../hooks/useConfirm';
import ConfirmModal from '../components/common/ConfirmModal';
import UserModal from '../components/users/UserModal';
import styles from '../styles/Dashboard.module.css';
import { TrashIcon, PencilSquareIcon, UserPlusIcon } from '@heroicons/react/24/outline';

const UsersPage: React.FC = () => {
    const [users, setUsers] = useState<IUser[]>([]);
    const [loading, setLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingUser, setEditingUser] = useState<IUser | null>(null);
    
    // Datos del usuario actual
    const currentUserEmail = localStorage.getItem('userEmail');
    // Obtenemos el rol del usuario logueado (asumiendo que lo guardaste o lo decodificas del token)
    // Si no lo guardaste en login, una opción rápida es buscarlo en la lista de usuarios cargada.
    // Por ahora, asumiremos que si puede ver esta página es Admin, pero validaremos en render.
    const [currentUserRole, setCurrentUserRole] = useState<string>('');

    const toast = useToast();
    const { isOpen, config, confirm, handleConfirm, handleCancel } = useConfirm();

    const fetchUsers = async () => {
        setLoading(true);
        try {
            const data = await getUsers();
            
            // 1. FILTRAR: Quitar al usuario logueado de la lista visible
            const filteredUsers = data.filter(u => u.email !== currentUserEmail);
            
            // Detectar mi rol actual basado en mi email
            const myself = data.find(u => u.email === currentUserEmail);
            if (myself) setCurrentUserRole(myself.rol);

            setUsers(filteredUsers);
        } catch (err: any) {
            toast.error('Error', 'No se pudieron cargar los usuarios');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchUsers();
    }, []);

    // --- Lógica CRUD ---

    const handleCreate = () => {
        setEditingUser(null);
        setIsModalOpen(true);
    };

    const handleEdit = (user: IUser) => {
        setEditingUser(user);
        setIsModalOpen(true);
    };

    const handleModalSubmit = async (userData: any) => {
        try {
            if (editingUser) {
                await updateUser(editingUser.id, userData);
                toast.success('Éxito', 'Usuario actualizado correctamente');
            } else {
                await createUser(userData);
                toast.success('Éxito', 'Usuario creado correctamente');
            }
            setIsModalOpen(false);
            fetchUsers();
        } catch (error: any) {
            toast.error('Error', error.message || 'Ocurrió un error');
        }
    };

    const handleDelete = async (id: number, name: string) => {
        const confirmed = await confirm({
            title: 'Eliminar Usuario',
            message: `¿Estás seguro de eliminar a ${name}? Esta acción es irreversible.`,
            confirmText: 'Eliminar',
            type: 'danger'
        });

        if (confirmed) {
            try {
                await deleteUser(id);
                toast.success('Éxito', 'Usuario eliminado');
                setUsers(prev => prev.filter(u => u.id !== id));
            } catch (error: any) {
                toast.error('Error', 'No se pudo eliminar el usuario');
            }
        }
    };

    // --- Renderizado condicional por Rol ---
    // Solo el ADMINISTRADOR puede ver botones de acción en esta página.
    const canManageUsers = currentUserRole === 'administrador';

    if (loading) return <AdminLayout pageTitle="Usuarios"><p>Cargando...</p></AdminLayout>;

    return (
        <AdminLayout pageTitle="Gestión de Usuarios">
            <div className={styles.mainContentHeader}>
                <h2 className={styles.headerTitle}>Usuarios del Sistema</h2>
                
                {/* Solo Admin puede crear */}
                {canManageUsers && (
                    <button className={styles.primaryButton} onClick={handleCreate}>
                        <UserPlusIcon style={{width: 20, height: 20}}/> Nuevo Usuario
                    </button>
                )}
            </div>

            <div className={styles.recentActivity}>
                {!canManageUsers && (
                    <div style={{padding: '1rem', background: '#fff3cd', color: '#856404', marginBottom: '1rem', borderRadius: '8px'}}>
                        Modo solo lectura: Solo los administradores pueden gestionar usuarios.
                    </div>
                )}

                <table className={styles.projectTable}>
                    <thead>
                        <tr>
                            <th>Nombre</th>
                            <th>Email</th>
                            <th>Rol</th>
                            {canManageUsers && <th>Acciones</th>}
                        </tr>
                    </thead>
                    <tbody>
                        {users.map((user) => (
                            <tr key={user.id}>
                                <td>
                                    <div style={{fontWeight: 'bold'}}>{user.name}</div>
                                </td>
                                <td>{user.email}</td>
                                <td>
                                    <span style={{
                                        padding: '4px 12px', 
                                        borderRadius: '20px', 
                                        fontSize: '0.85rem',
                                        fontWeight: '600',
                                        backgroundColor: 
                                            user.rol === 'administrador' ? '#dbeafe' : 
                                            user.rol === 'editor' ? '#dcfce7' : '#f3f4f6',
                                        color: 
                                            user.rol === 'administrador' ? '#1e40af' : 
                                            user.rol === 'editor' ? '#166534' : '#374151',
                                    }}>
                                        {user.rol.charAt(0).toUpperCase() + user.rol.slice(1)}
                                    </span>
                                </td>
                                {canManageUsers && (
                                    <td>
                                        <div className={styles.actionsCell}>
                                            <button onClick={() => handleEdit(user)} className={styles.editButton} title="Editar">
                                                <PencilSquareIcon className={styles.iconButtonIcon} />
                                            </button>
                                            <button onClick={() => handleDelete(user.id, user.name)} className={styles.deleteButton} title="Eliminar">
                                                <TrashIcon className={styles.iconButtonIcon} />
                                            </button>
                                        </div>
                                    </td>
                                )}
                            </tr>
                        ))}
                    </tbody>
                </table>
                {users.length === 0 && <p style={{textAlign: 'center', padding: '2rem', color: '#666'}}>No hay otros usuarios registrados.</p>}
            </div>

            {/* Modales */}
            <UserModal 
                isOpen={isModalOpen} 
                onClose={() => setIsModalOpen(false)} 
                onSubmit={handleModalSubmit}
                isEditing={!!editingUser}
                initialData={editingUser}
            />

            <ConfirmModal
                isOpen={isOpen}
                title={config?.title || ''}
                message={config?.message || ''}
                confirmText={config?.confirmText}
                type={config?.type}
                onConfirm={handleConfirm}
                onCancel={handleCancel}
            />
        </AdminLayout>
    );
};

export default UsersPage;